import type { CharacterDef, InputState, RaceConfig, RacePhase, RaceResult, RaceResultEntry, ItemEvent, Vec2 } from '../core/types';
import { IDLE_INPUT } from '../core/types';
import type { Track } from './track';
import { Kart } from './kart';
import { ItemSystem } from './items';
import { createAi, type AiBrain, type AiView } from './ai';
import { RIVALS, CHARACTERS, characterById } from '../data/roster';
import { Rng } from '../core/rng';

export interface WorldOptions {
  track: Track;
  config: RaceConfig;
}

export const TEAM_NAMES = ['PIROS', 'KÉK'] as const;
export const TEAM_COLORS = ['#ff5c4d', '#3ec6c0'] as const;
export const BATTLE_LIVES = 3;
export const BATTLE_TIME = 150;

/**
 * A verseny teljes szimulációja: rajtrács, visszaszámlálás, körök, ütközések,
 * itemek, helyezések, befutó. A renderelés csak olvassa.
 */
export class World {
  readonly track: Track;
  readonly config: RaceConfig;
  readonly karts: Kart[] = [];
  readonly players: Kart[] = [];
  readonly items: ItemSystem;
  readonly brains = new Map<number, AiBrain>();
  /** online: a kilépett ember kocsiját AI viszi tovább (nincs üres hely a pályán) */
  readonly aiDriven = new Set<number>();
  readonly rng: Rng;

  phase: RacePhase = 'countdown';
  time = 0;
  countdown = 3.0;
  goTimer = 0;
  lapStart = 0;

  standings: Kart[] = [];
  events: ItemEvent[] = [];
  result: RaceResult | null = null;
  /** kart-kart ütközések a következő rendereléshez/hanghoz */
  bumps: { at: Vec2; strength: number }[] = [];

  /** battle: csapatonkénti hátralévő életek és visszaszámláló */
  readonly battle: boolean;
  battleLives: [number, number] = [0, 0];
  battleTimeLeft = BATTLE_TIME;

  private finishCount = 0;
  private finishTimer = 0;
  private grid: { pos: Vec2; heading: number; s: number }[] = [];

  constructor(opts: WorldOptions) {
    this.track = opts.track;
    this.config = opts.config;
    this.rng = new Rng(opts.config.seed);
    this.items = new ItemSystem(this.track, opts.config.seed ^ 0x5bd1e995);
    this.battle = opts.config.battle === true;

    const humans: CharacterDef[] = opts.config.picks.map((id) => characterById(id) ?? CHARACTERS[0]);
    const rivalPool = [...RIVALS];
    const rng = new Rng(opts.config.seed ^ 0x27d4eb2f);
    for (let i = rivalPool.length - 1; i > 0; i--) {
      const j = Math.floor(rng.next() * (i + 1));
      [rivalPool[i], rivalPool[j]] = [rivalPool[j], rivalPool[i]];
    }
    const aiAllowed = !opts.config.practice && !opts.config.noAi;
    const aiCount = this.battle
      ? aiAllowed
        ? Math.max(0, 4 - humans.length)
        : 0
      : opts.config.practice
        ? 0
        : aiAllowed
          ? Math.max(0, 6 - humans.length)
          : 0;
    const aiChars = rivalPool.slice(0, aiCount);

    // a rajtrácsot összekeverjük: senki ne legyen állandóan elöl vagy hátul
    const all: { char: CharacterDef; isPlayer: boolean; playerIndex: number }[] = [
      ...aiChars.map((c) => ({ char: c, isPlayer: false, playerIndex: -1 })),
      ...humans.map((c, i) => ({ char: c, isPlayer: true, playerIndex: i })),
    ];
    for (let i = all.length - 1; i > 0; i--) {
      const j = Math.floor(rng.next() * (i + 1));
      [all[i], all[j]] = [all[j], all[i]];
    }

    this.placeGrid(all.length);

    const grid = this.grid;
    all.forEach((entry, i) => {
      const g = grid[i];
      const kart = new Kart({
        id: i,
        character: entry.char,
        isPlayer: entry.isPlayer,
        playerIndex: entry.playerIndex,
        pos: g.pos,
        heading: g.heading,
      });
      kart.lap = 1;
      kart.progress = this.track.progressOfS(g.s - this.track.startS);
      kart.totalProgress = 1 + kart.progress;
      kart.lapStartTime = 0;
      this.karts.push(kart);
      if (entry.isPlayer) this.players.push(kart);
      if (!entry.isPlayer || this.config.autopilot) {
        const skill = 0.72 + this.config.difficulty * 0.24;
        this.brains.set(kart.id, createAi(entry.char, opts.config.seed + i * 977, skill));
      }
    });

    if (this.battle) {
      // emberek váltakozó csapatban (P1/P3 piros, P2/P4 kék), az AI-t kiegyensúlyozzuk
      const counts: [number, number] = [0, 0];
      for (const kart of this.karts) {
        kart.battleMode = true;
        kart.lives = BATTLE_LIVES;
        if (kart.isPlayer) {
          kart.team = kart.playerIndex % 2;
          counts[kart.team]++;
        }
      }
      for (const kart of this.karts) {
        if (kart.isPlayer) continue;
        kart.team = counts[0] <= counts[1] ? 0 : 1;
        counts[kart.team]++;
      }
      this.updateTeamLives();
    }

    this.updateStandings();
    this.players.sort((a, b) => a.playerIndex - b.playerIndex);
  }

  private placeGrid(count: number): void {
    const out: { pos: Vec2; heading: number; s: number }[] = [];
    const startS = this.track.startS + 90;
    for (let i = 0; i < count; i++) {
      const row = Math.floor(i / 2);
      const col = i % 2;
      const s = startS + row * 74;
      const progress = this.track.progressOfS(s);
      const lateral = col === 0 ? -54 : 54;
      const pos = this.track.posAt(progress, lateral);
      const tangent = this.track.tangentAt(progress);
      out.push({ pos, heading: Math.atan2(tangent.y, tangent.x), s });
    }
    this.grid = out;
  }

  setPlayerInput(playerIndex: number, input: InputState): void {
    const kart = this.players[playerIndex];
    if (kart && !kart.finished && !kart.out && kart.respawnTimer <= 0) kart.setInput(input);
  }

  /**
   * Online: ha egy ember kilép, a kocsija nem tűnik el a mezőnyből – az AI
   * veszi át, így a verseny ugyanazzal a 6 fős mezőnnyel folytatódik.
   */
  takeOver(kartId: number): void {
    const kart = this.karts.find((k) => k.id === kartId);
    if (!kart) return;
    this.aiDriven.add(kartId);
    if (!this.brains.has(kartId)) {
      this.brains.set(
        kartId,
        createAi(kart.character, this.config.seed + kartId * 977, 0.82),
      );
    }
  }

  step(dt: number): void {
    if (this.phase === 'countdown') {
      this.countdown -= dt;
      if (this.countdown <= 0) {
        this.countdown = 0;
        this.phase = 'racing';
        this.goTimer = 1.2;
        this.time = 0;
        this.lapStart = 0;
        for (const k of this.karts) k.lapStartTime = 0;
      }
      return;
    }
    if (this.phase !== 'racing') return;

    this.time += dt;
    this.goTimer = Math.max(0, this.goTimer - dt);

    if (this.battle) {
      this.battleTimeLeft = Math.max(0, this.battleTimeLeft - dt);
      this.updateRespawns(dt);
      if (this.battleTimeLeft <= 0) {
        this.finishBattle();
        return;
      }
    }

    const view = this.buildAiView();
    for (const kart of this.karts) {
      if (kart.finished || kart.out || kart.respawnTimer > 0) continue;
      if (!kart.isPlayer || this.config.autopilot || this.aiDriven.has(kart.id)) {
        const brain = this.brains.get(kart.id);
        if (brain) kart.setInput(brain.update(dt, kart, view));
      }
      const prevProgress = kart.progress;
      kart.update(dt, this.track);

      if (kart.input.useItem) this.tryUseItem(kart);
      if (kart.input.ability) this.tryAbility(kart);

      if (this.battle) kart.totalProgress = kart.progress;
      else this.updateLap(kart, prevProgress);
    }

    const active = this.karts.filter((k) => !k.out && k.respawnTimer <= 0);
    const events = this.items.update(dt, active);
    if (events.length) this.events.push(...events);
    if (this.battle && events.length) this.applyBattleDamage(events);

    this.resolveCollisions();
    this.updateStandings();
    if (this.battle) this.checkBattleEnd();
    else this.checkRaceEnd(dt);
  }

  /** battle: találatok életet vonnak le (csapattárs ellen nem) */
  private applyBattleDamage(events: ItemEvent[]): void {
    for (const event of events) {
      if (event.kind !== 'hit' || event.targetId === undefined) continue;
      const target = this.karts.find((k) => k.id === event.targetId);
      if (!target || target.out || target.respawnTimer > 0) continue;
      const attacker = event.ownerId !== undefined ? this.karts.find((k) => k.id === event.ownerId) : undefined;
      if (attacker && attacker.team === target.team) continue;
      target.lives -= 1;
      if (target.lives <= 0) {
        target.lives = 0;
        target.out = true;
        target.vel = { x: 0, y: 0 };
      } else {
        target.respawnTimer = 1.6;
      }
    }
    this.updateTeamLives();
  }

  private updateRespawns(dt: number): void {
    for (const kart of this.karts) {
      if (kart.out || kart.respawnTimer <= 0) continue;
      kart.respawnTimer -= dt;
      if (kart.respawnTimer <= 0) this.respawnKart(kart);
    }
  }

  private respawnKart(kart: Kart): void {
    const progress = this.rng.range(0, 1);
    const lateral = this.rng.range(-60, 60);
    kart.pos = this.track.posAt(progress, lateral);
    const tangent = this.track.tangentAt(progress);
    kart.heading = Math.atan2(tangent.y, tangent.x);
    kart.vel = { x: 0, y: 0 };
    kart.respawnTimer = 0;
    kart.shieldTimer = Math.max(kart.shieldTimer, 2);
  }

  private updateTeamLives(): void {
    this.battleLives = [0, 0];
    for (const kart of this.karts) {
      if (kart.out) continue;
      this.battleLives[kart.team] += kart.lives;
    }
  }

  private checkBattleEnd(): void {
    if (this.phase !== 'racing') return;
    if (this.battleLives[0] <= 0 || this.battleLives[1] <= 0) this.finishBattle();
  }

  finishBattle(): void {
    if (this.phase === 'finished') return;
    this.phase = 'finished';
    this.updateTeamLives();
    const [red, blue] = this.battleLives;
    const winningTeam = red === blue ? -1 : red > blue ? 0 : 1;

    this.standings = [...this.karts].sort((a, b) => {
      if (winningTeam >= 0) {
        if (a.team !== b.team) return a.team === winningTeam ? -1 : 1;
      }
      if (a.out !== b.out) return a.out ? 1 : -1;
      if (b.lives !== a.lives) return b.lives - a.lives;
      return b.totalProgress - a.totalProgress;
    });

    const entries: RaceResultEntry[] = this.standings.map((k, i) => ({
      kartId: k.id,
      name: k.character.name,
      short: k.character.short,
      color: TEAM_COLORS[k.team],
      characterId: k.character.id,
      isPlayer: k.isPlayer,
      place: i + 1,
      time: 0,
      bestLap: null,
      laps: 0,
      finished: !k.out,
      lives: k.lives,
      team: k.team,
    }));

    this.result = {
      trackName: this.track.def.name,
      mode: this.config.mode,
      online: this.config.online === true,
      entries,
      battle: {
        winningTeam,
        teams: [
          { name: TEAM_NAMES[0], color: TEAM_COLORS[0], lives: red },
          { name: TEAM_NAMES[1], color: TEAM_COLORS[1], lives: blue },
        ],
      },
    };
  }

  private buildAiView(): AiView {
    const active = this.karts.filter((k) => !k.out && k.respawnTimer <= 0);
    return {
      track: this.track,
      karts: active,
      time: this.time,
      hazards: this.items.hazards.map((h) => ({ pos: h.pos, radius: h.radius })),
      projectiles: this.items.projectiles.map((p) => ({ pos: p.pos, radius: p.radius })),
      boxes: this.items.boxes.map((b) => ({ pos: b.pos, active: b.active })),
    };
  }

  private tryUseItem(kart: Kart): void {
    if (!kart.item || kart.itemRollTimer > 0) return;
    const events = this.items.use(kart, this.karts);
    if (events.length) this.events.push(...events);
  }

  private tryAbility(kart: Kart): void {
    if (this.config.noAbilities) return;
    if (kart.abilityCooldown > 0) return;
    if (!kart.activateAbility()) return;
    switch (kart.character.ability.id) {
      case 'dash':
        kart.applyBoost(0.9, 1.26);
        break;
      case 'nitro':
        kart.applyBoost(1.25, 1.2);
        break;
      case 'shield':
        kart.shieldTimer = Math.max(kart.shieldTimer, 4);
        break;
      case 'hop':
        // az ugrás átviszi a járdán, és egy kis lökést is ad
        kart.applyBoost(0.95, 1.24);
        break;
      default:
        break;
    }
  }

  private updateLap(kart: Kart, prevProgress: number): void {
    const cur = kart.progress;
    if (cur > 0.5) kart.lapCheckpoint = true;

    if (prevProgress > 0.7 && cur < 0.3) {
      // csak akkor számolunk új kört, ha a fél távot már teljesítette
      // (így a rajtvonalnál oda-vissza lökdösött kart nem kap hamis kört)
      if (!kart.lapCheckpoint) {
        kart.lapCheckpoint = false;
        return;
      }
      kart.lapCheckpoint = false;
      kart.lap += 1;
      const lapTime = this.time - kart.lapStartTime;
      if (lapTime > 5) {
        kart.lapTimes.push(lapTime);
        kart.bestLap = kart.bestLap === null ? lapTime : Math.min(kart.bestLap, lapTime);
      }
      kart.lapStartTime = this.time;
      if (kart.lap > this.config.laps && !kart.finished) this.finishKart(kart);
    } else if (prevProgress < 0.3 && cur > 0.7) {
      kart.lap = Math.max(1, kart.lap - 1);
      kart.lapCheckpoint = false;
    }
    kart.totalProgress = kart.lap + cur;
  }

  private finishKart(kart: Kart): void {
    kart.finished = true;
    kart.finishTime = this.time;
    this.finishCount += 1;
    kart.setInput({ ...IDLE_INPUT });
    kart.vel = { x: 0, y: 0 };
    kart.boostTimer = 0;
    // kivonjuk a forgalomból: a pályán kívülre, láthatatlan helyre
    kart.pos = { x: this.track.bounds.minX - 2000, y: this.track.bounds.minY - 2000 };
  }

  private resolveCollisions(): void {
    const ks = this.karts;
    for (let i = 0; i < ks.length; i++) {
      const a = ks[i];
      if (a.finished || a.out || a.respawnTimer > 0) continue;
      for (let j = i + 1; j < ks.length; j++) {
        const b = ks[j];
        if (b.finished || b.out || b.respawnTimer > 0) continue;
        const dx = b.pos.x - a.pos.x;
        const dy = b.pos.y - a.pos.y;
        const minD = a.radius + b.radius;
        const d2 = dx * dx + dy * dy;
        if (d2 >= minD * minD || d2 < 0.0001) continue;
        const d = Math.sqrt(d2);
        const nx = dx / d;
        const ny = dy / d;
        const overlap = minD - d;
        const total = a.stats.mass + b.stats.mass;
        const pushA = (b.stats.mass / total) * overlap;
        const pushB = (a.stats.mass / total) * overlap;
        a.pos.x -= nx * pushA;
        a.pos.y -= ny * pushA;
        b.pos.x += nx * pushB;
        b.pos.y += ny * pushB;

        const rvx = b.vel.x - a.vel.x;
        const rvy = b.vel.y - a.vel.y;
        const rvn = rvx * nx + rvy * ny;
        if (rvn < 0) {
          const e = 0.32;
          const impulse = (-(1 + e) * rvn) / (1 / a.stats.mass + 1 / b.stats.mass);
          a.vel.x -= (nx * impulse) / a.stats.mass;
          a.vel.y -= (ny * impulse) / a.stats.mass;
          b.vel.x += (nx * impulse) / b.stats.mass;
          b.vel.y += (ny * impulse) / b.stats.mass;
          a.vel.x *= 0.985;
          a.vel.y *= 0.985;
          b.vel.x *= 0.985;
          b.vel.y *= 0.985;
          if (impulse > 2400) {
            const light = a.stats.mass < b.stats.mass ? a : b;
            light.heading += ((a.id + b.id) % 2 === 0 ? -1 : 1) * 0.1;
          }
          // ütközés-visszajelzés (hang + szikra), hogy ne tűnjön ok nélküli lassulásnak
          if (-rvn > 70) {
            this.bumps.push({
              at: { x: (a.pos.x + b.pos.x) / 2, y: (a.pos.y + b.pos.y) / 2 },
              strength: Math.min(1, -rvn / 260),
            });
          }
        }
      }
    }
  }

  private updateStandings(): void {
    if (this.battle) {
      this.standings = [...this.karts].sort((a, b) => {
        if (a.out !== b.out) return a.out ? 1 : -1;
        if (b.lives !== a.lives) return b.lives - a.lives;
        return b.totalProgress - a.totalProgress;
      });
      return;
    }
    this.standings = [...this.karts].sort((a, b) => {
      if (a.finished && b.finished) return a.finishTime - b.finishTime;
      if (a.finished) return -1;
      if (b.finished) return 1;
      return b.totalProgress - a.totalProgress;
    });
  }

  private checkRaceEnd(dt: number): void {
    const allDone = this.karts.every((k) => k.finished);
    const humansDone = this.players.length > 0 && this.players.every((p) => p.finished);
    if (allDone) {
      this.finishNow();
      return;
    }
    if (humansDone) {
      this.finishTimer += dt;
      if (this.finishTimer > 2.6) this.finishNow();
    } else if (this.time > 320) {
      this.finishNow();
    }
  }

  finishNow(): void {
    if (this.phase === 'finished') return;
    this.phase = 'finished';
    this.updateStandings();
    const entries: RaceResultEntry[] = this.standings.map((k, i) => ({
      kartId: k.id,
      name: k.character.name,
      short: k.character.short,
      color: k.character.colors.body,
      characterId: k.character.id,
      isPlayer: k.isPlayer,
      place: i + 1,
      time: k.finished ? k.finishTime : 0,
      bestLap: k.bestLap,
      laps: Math.min(k.lap, this.config.laps),
      finished: k.finished,
    }));
    this.result = {
      trackName: this.track.def.name,
      mode: this.config.mode,
      online: this.config.online === true,
      entries,
    };
  }

  /** a HUD-nak: 3,2,1,0 (GO), vagy null */
  hudCountdown(): number | null {
    if (this.phase === 'countdown') return Math.max(1, Math.ceil(this.countdown));
    if (this.goTimer > 0) return 0;
    return null;
  }

  positionOf(kart: Kart): number {
    return this.standings.indexOf(kart) + 1;
  }

  drainEvents(): ItemEvent[] {
    if (!this.events.length) return [];
    const out = this.events;
    this.events = [];
    return out;
  }

  drainBumps(): { at: Vec2; strength: number }[] {
    if (!this.bumps.length) return [];
    const out = this.bumps;
    this.bumps = [];
    return out;
  }
}

type CharacterDefs = (typeof CHARACTERS)[number][];
